// KitchenCard — the single-item card rendered in ItemGrid for both
// drilled-tile views and search results. Tap opens the editor
// (MCMItemCard); the inline fill-gauge slider degrades to read-only
// when onUpdate isn't wired (Showcase mode).
//
// Horizontal swipe-to-reveal-Remove was removed in the migration to
// MCMItemCard: the framer drag was unreliable on iOS (directional
// lock + 12 px grid gap dead zone) and "tap to open, delete inside
// the editor" is the cleaner flow.

import { memo, useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import {
  GlassPanel, StatusDot, TintedPill, statusTintOverlay, withAlpha,
} from "./primitives";
import { useTheme } from "./theme";
import { font, space, radius } from "./tokens";
import { findIngredient } from "../../data/ingredients";
import { useIngredientInfo } from "../../lib/useIngredientInfo";
import { canonicalImageUrlFor } from "../../lib/canonicalIcons";
import {
  shelfLifeFor, isRecent, formatDaysChip, daysChipColor,
  buildDisplayNameParts,
} from "./helpers";
import { BrandPickerSheet } from "./BrandPickerSheet";
import {
  isPinned as isAiPinned,
  togglePinned as toggleAiPin,
  subscribePinned,
} from "../../lib/aiRecipePinned";

// Long-press knobs — copied from CalendarDay.jsx's drag-arm pattern.
// 400ms is long enough to feel intentional and not catch quick taps;
// 6px movement tolerance lets a finger settle without cancelling.
const LONG_PRESS_MS = 400;
const LONG_PRESS_MOVE_TOLERANCE = 6;
// After the flip animation fires, swallow back-panel taps for this
// long. Otherwise the same touch that triggered the long-press would
// release as a click on the back face and flip it right back.
const POST_FLIP_CLICK_LOCKOUT_MS = 500;
// iOS Safari text-selection suppression triple. Inheritance from the
// outer wrapper is unreliable; callers re-apply on inner faces.
const NO_SELECT_STYLE = {
  userSelect: "none",
  WebkitUserSelect: "none",
  WebkitTouchCallout: "none",
};

function KitchenCardImpl({
  item,
  onPick,
  tileLabel = null,
  // Inline update — called with a partial patch when the user
  // adjusts the row in place. Currently wired to the tappable
  // fill gauge; when null the gauge is read-only.
  onUpdate = null,
  // Long-press hold-menu callbacks. Both optional — when omitted,
  // the corresponding back-face button doesn't render. Parents
  // (App.jsx) wire onDelete to setPantry-filter and onRestock to
  // setShoppingList-append. When neither is wired AND the card has
  // no canonical (no AI-recipe pin target), the long-press flip is
  // suppressed so the card stays a plain tap-to-edit affordance.
  onDelete = null,
  onRestock = null,
  // Bulk-select entry — when provided, LONG-PRESS enters kitchen
  // select mode (with this card selected) instead of flipping to the
  // focus panel. The panel's actions (To Recipe / Restock / Delete)
  // live on the select-mode bar now; double-tap still flips for the
  // single-item shortcut.
  onHoldSelect = null,
  // Distinct brands across the user's pantry — used as the
  // suggestion list when the "+ ADD BRAND" affordance opens
  // the BrandPickerSheet. Empty array is fine (the sheet falls
  // back to a "type one to start" empty-state copy).
  brandSuggestions = [],
  // Stack collapsing — set by ItemGrid when this card represents
  // multiple identical pantry rows (same canonical + brand + size
  // + claims + tile). stackCount > 1 renders a "×N →" badge above
  // the title; tap fires onOpenStack so the kitchen-screen parent
  // can mount StackDetailSheet with the full member list. When
  // stackCount is 1 (singleton) or onOpenStack is null, the badge
  // doesn't render.
  stackCount = 1,
  onOpenStack = null,
}) {
  const { theme } = useTheme();
  // Canonical nutrition lookup — DB-approved info wins, bundled
  // canonical metadata fills the gap. Returns the kcal anchor
  // (per 100g / per count / etc.) so the card can surface a
  // single quick calorie chip without rolling out the full
  // breakdown panel here.
  const { getInfo } = useIngredientInfo();
  const nutrition = (() => {
    if (!item?.canonicalId) return null;
    const dbInfo = getInfo(item.canonicalId);
    const bundled = findIngredient(item.canonicalId);
    return dbInfo?.nutrition || bundled?.nutrition || null;
  })();
  const warn = item.status === "warn";
  // Warn cards pick up a gentle theme-derived burnt wash so
  // "expires soon" is noticeable at the card level without being
  // alarming. Wash follows time-of-day automatically.
  const warnOverlay = warn ? statusTintOverlay(theme, "warn") : null;
  // Bundled SVG icon for the item's canonical id if one exists
  // (public/icons/<canonical>.svg, registered in canonicalIcons
  // BUNDLED_ICON_SLUGS). Admin-generated images from
  // ingredient_info.imageUrl aren't threaded through yet — that
  // needs the IngredientInfo context, tracked as a follow-up.
  // Emoji is always the fallback.
  const canonicalId = item?._raw?.canonicalId || null;
  const iconUrl = canonicalImageUrlFor(canonicalId, null);

  // Inline fill-gauge editing — toggled by tapping the gauge
  // bar. Reveals a small slider underneath that drags the row's
  // amount between 0 and max. Live updates fire through onUpdate
  // so the user sees the bar redraw as they slide. Disabled
  // when onUpdate isn't wired (Showcase mode).
  const [fillEditing, setFillEditing] = useState(false);
  // Brand picker is a modal sheet; this latch decides whether
  // BrandPickerSheet is mounted. Tap the "+ ADD BRAND" chip to
  // open, picker calls onClose to dismiss.
  const [brandEditing, setBrandEditing] = useState(false);
  const updateEnabled = typeof onUpdate === "function";

  // Flip / focus-mode state. Card has a front (existing layout, tap
  // opens editor) and a back (3 action buttons: delete / add to
  // recipe / restock). Triggers to enter focus mode:
  //   • double-tap on the front
  //   • long-press (≥400ms hold, ≤6px movement) on the front
  // Exit focus mode:
  //   • tap on the back's empty area (NOT on a button)
  // Per the user's "make it unopenable from tap" rule, while
  // flipped a single tap on the front is a no-op — the user must
  // explicitly exit focus mode to re-enable the tap-to-open path.
  const [flipped, setFlipped] = useState(false);
  // Wall-clock when we last entered focus mode. Used to swallow the
  // synthetic click that fires on the back panel right after the
  // long-press / double-tap that triggered the flip — without this,
  // the same touch's release would land on the back panel's
  // tap-to-exit handler and immediately unflip the card.
  const [flippedAt, setFlippedAt] = useState(0);
  // Gate the back face's pointer events. When the user long-presses
  // a card, their finger is still down WHEN the flip fires — on
  // release, the synthetic click lands on whatever back-face element
  // sits at the finger's position (commonly one of the action
  // buttons), firing it unintentionally. While `backInteractive` is
  // false the entire back face is `pointer-events: none`, so the
  // release click hits dead air. Flips true after a short lockout
  // (≈animation duration), then the user can tap deliberately.
  const [backInteractive, setBackInteractive] = useState(false);
  const backInteractiveTimer = useRef(null);

  // Long-press detection refs — adapted from CalendarDay.jsx's
  // drag-arm pattern. Timer fires at LONG_PRESS_MS, cancels on
  // movement >= LONG_PRESS_MOVE_TOLERANCE px from the pointer-down
  // origin or on pointerup/cancel/leave.
  const longPressTimer = useRef(null);
  const longPressOrigin = useRef(null);
  const longPressFired = useRef(false);

  // Double-tap detection. Single tap schedules the "open editor"
  // after a short window; a second tap inside that window cancels
  // the open and flips instead. The window must be tight enough that
  // single-tap latency stays acceptable (~220ms is the iOS double-
  // tap-zoom threshold; below 300ms feels responsive).
  const DOUBLE_TAP_WINDOW_MS = 250;
  const tapPendingTimer = useRef(null);

  // Two-tap arm-then-commit for the destructive Delete on the back.
  // First tap arms; second tap within DELETE_ARM_MS commits; auto-
  // disarms after the window. Matches MCMItemCard's existing pattern
  // so users see consistent destructive-confirm behavior across
  // both delete surfaces.
  const DELETE_ARM_MS = 4000;
  const [deleteArmed, setDeleteArmed] = useState(false);
  useEffect(() => {
    if (!deleteArmed) return undefined;
    const t = setTimeout(() => setDeleteArmed(false), DELETE_ARM_MS);
    return () => clearTimeout(t);
  }, [deleteArmed]);
  // Reset the arm whenever the card flips back to front so re-flipping
  // doesn't surface a stale armed state.
  useEffect(() => { if (!flipped) setDeleteArmed(false); }, [flipped]);

  // AI-recipe pin state — tracks whether THIS canonical is in the
  // device-local pin set. Subscribed so toggles from elsewhere keep
  // the back-face button label honest. The lib is not user-
  // partitioned (single localStorage key), so undefined userId is
  // fine here.
  const [pinned, setPinned] = useState(() => isAiPinned(canonicalId));
  useEffect(() => {
    setPinned(isAiPinned(canonicalId));
    return subscribePinned(() => setPinned(isAiPinned(canonicalId)));
  }, [canonicalId]);

  // Focus-mode trigger. Centralized so both long-press and
  // double-tap flow through the same animation + lockout.
  const enterFocusMode = () => {
    if (flipped) return;
    setFlipped(true);
    setFlippedAt(Date.now());
    setBackInteractive(false);
    if (backInteractiveTimer.current) clearTimeout(backInteractiveTimer.current);
    backInteractiveTimer.current = setTimeout(() => {
      setBackInteractive(true);
      backInteractiveTimer.current = null;
    }, POST_FLIP_CLICK_LOCKOUT_MS);
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      try { navigator.vibrate(10); } catch (_) { /* iOS quietly rejects */ }
    }
  };
  const exitFocusMode = () => {
    setFlipped(false);
    setBackInteractive(false);
    if (backInteractiveTimer.current) {
      clearTimeout(backInteractiveTimer.current);
      backInteractiveTimer.current = null;
    }
  };

  // Whether the long-press gesture is even worth arming on this card.
  // No actions wired AND no canonical → flip would reveal an empty
  // back face, so leave the card as a plain tap-to-edit affordance.
  const focusEnabled = !!(onHoldSelect || onDelete || onRestock || canonicalId);

  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    longPressOrigin.current = null;
  };
  const handlePointerDown = (e) => {
    if (!focusEnabled) return;
    if (flipped) return;             // already in focus mode
    if (fillEditing) return;          // user is mid-slider, don't intercept
    longPressFired.current = false;
    longPressOrigin.current = { x: e.clientX, y: e.clientY };
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      longPressTimer.current = null;
      if (onHoldSelect) {
        // Hold → bulk select. Same haptic cue the flip used so the
        // gesture still "lands" in the hand.
        if (typeof navigator !== "undefined" && navigator.vibrate) {
          try { navigator.vibrate(10); } catch (_) { /* iOS rejects */ }
        }
        onHoldSelect(item);
      } else {
        enterFocusMode();
      }
    }, LONG_PRESS_MS);
  };
  const handlePointerMove = (e) => {
    if (!longPressOrigin.current) return;
    const dx = e.clientX - longPressOrigin.current.x;
    const dy = e.clientY - longPressOrigin.current.y;
    if (Math.hypot(dx, dy) > LONG_PRESS_MOVE_TOLERANCE) cancelLongPress();
  };
  const handlePointerEnd = () => cancelLongPress();
  // Cleanup on unmount so a pending timer doesn't fire after the row
  // is removed.
  useEffect(() => () => {
    cancelLongPress();
    if (tapPendingTimer.current) {
      clearTimeout(tapPendingTimer.current);
      tapPendingTimer.current = null;
    }
    if (backInteractiveTimer.current) {
      clearTimeout(backInteractiveTimer.current);
      backInteractiveTimer.current = null;
    }
  }, []);

  const handleClick = (e) => {
    // Tapping the card opens the editor — UNLESS the inline
    // fill-gauge slider is expanded, in which case the surrounding
    // tap closes it first. Prevents the jarring "I tapped near
    // the slider and the whole card opened" experience.
    if (fillEditing) {
      e.stopPropagation();
      setFillEditing(false);
      return;
    }
    // Long-press just fired and flipped the card — eat the synthetic
    // click that follows the pointerup so it doesn't propagate to the
    // back panel's tap-to-exit handler and undo the flip.
    if (longPressFired.current) {
      longPressFired.current = false;
      e.stopPropagation();
      return;
    }
    // While in focus mode, a tap on the front-face is a no-op. The
    // user must exit focus mode (tap the back's empty area) before
    // tap-to-open works again. The front face has pointerEvents:none
    // when flipped so this rarely fires; defense in depth.
    if (flipped) return;
    // Double-tap detection. Two taps within DOUBLE_TAP_WINDOW_MS
    // cancel the pending editor-open and flip instead.
    if (focusEnabled && tapPendingTimer.current) {
      clearTimeout(tapPendingTimer.current);
      tapPendingTimer.current = null;
      enterFocusMode();
      return;
    }
    if (focusEnabled) {
      // First tap — schedule the open after the double-tap window
      // closes, so a follow-up tap inside the window can cancel it.
      tapPendingTimer.current = setTimeout(() => {
        tapPendingTimer.current = null;
        if (onPick) onPick(item);
      }, DOUBLE_TAP_WINDOW_MS);
      return;
    }
    // Card has no focus-mode actions wired — open immediately, no
    // double-tap latency.
    if (onPick) onPick(item);
  };

  // Back-panel empty-area tap → exit focus mode. Buttons inside the
  // back panel stopPropagation so this only fires when the user
  // taps actual blank space.
  const handleBackPanelClick = () => {
    if (Date.now() - flippedAt < POST_FLIP_CLICK_LOCKOUT_MS) return;
    exitFocusMode();
  };

  // Action handlers — fire the parent callback, optionally flip back.
  // Delete is gated by a two-tap arm; first tap sets the armed state
  // (auto-disarms after DELETE_ARM_MS), second tap commits.
  const handleDeleteTap = (e) => {
    e.stopPropagation();
    if (!deleteArmed) {
      setDeleteArmed(true);
      return;
    }
    setDeleteArmed(false);
    if (onDelete) onDelete(item._raw || item);
    // Card unmounts as the row leaves pantry — no flip-back needed.
  };
  const handlePinTap = (e) => {
    e.stopPropagation();
    if (!canonicalId) return;
    toggleAiPin(canonicalId);
    // Brief pause so the user sees the ✓ state register before the
    // card flips back to its normal face.
    setTimeout(() => exitFocusMode(), 600);
  };
  const handleRestockTap = (e) => {
    e.stopPropagation();
    if (onRestock) onRestock(item._raw || item);
    setTimeout(() => exitFocusMode(), 200);
  };

  return (
    <>
    <div
      // Spoilage aura — fixed-size green halo that lingers around
      // the card's edge when the item is warn. The visual is the
      // same gentle ~3.6s hum as before, but the implementation
      // moved off animated box-shadow (paint-bound, repaints the
      // card every frame and tanks scroll perf when several warn
      // cards are visible at once) to a dedicated overlay sibling
      // whose OPACITY pulses. Opacity is GPU-composited — the
      // browser caches the static shadow once and just blends a
      // single layer alpha per frame, so the grid stays at refresh
      // rate even with a dozen cards humming in lockstep.
      style={{
        position: "relative",
        borderRadius: 22, // match GlassPanel rounding so the
                          // glow contour follows the card edge
                          // rather than spilling square corners
        // Anchor 3D depth for the inner flip container. Without
        // perspective, rotateY just shears the element flat instead
        // of feeling like a card turning over.
        perspective: 1000,
        // Suppress text selection + iOS magnifier on the long-press
        // gesture. Triple-pronged because Safari ignores standard
        // userSelect inheritance from positioned ancestors. Re-
        // applied on the inner faces (defense in depth — see
        // CalendarDay.jsx for the same pattern).
        ...NO_SELECT_STYLE,
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      onPointerLeave={handlePointerEnd}
    >
      {warn && (
        <div
          aria-hidden
          className="mcm-warn-aura"
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 22,
            boxShadow:
              "0 0 16px 3px rgba(123,156,92,0.70), 0 0 32px 6px rgba(123,156,92,0.40)",
            pointerEvents: "none",
          }}
        />
      )}
    {/* Flip container — front + back rotate together on Y axis.
        transformStyle: preserve-3d so the back face's own
        rotateY(180deg) composes in 3D space (without it, the back
        renders flat over the front and never disappears at 90°).
        backfaceVisibility on each face hides it when its angle
        passes 90° from the camera. */}
    <motion.div
      style={{
        position: "relative",
        width: "100%",
        transformStyle: "preserve-3d",
        WebkitTransformStyle: "preserve-3d",
      }}
      animate={{ rotateY: flipped ? 180 : 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    >
    <div style={{
      backfaceVisibility: "hidden",
      WebkitBackfaceVisibility: "hidden",
      // While flipped, the front face still occupies its grid slot
      // (drives the wrapper's height) but stops capturing taps so
      // the synthetic post-flip click doesn't reach handleClick.
      pointerEvents: flipped ? "none" : "auto",
      ...NO_SELECT_STYLE,
    }}>
    <GlassPanel
      flat
      interactive
      onClick={handleClick}
      padding={24}
      style={{
        // Horizontal layout — icon left, text column right with
        // stacked name + meta row. Padding 24 across (was 18) gives
        // the title and meta row generous breathing room from every
        // edge so the row reads as a card, not a flush list.
        // minHeight 104 absorbs the extra vertical chrome.
        position: "relative",
        display: "flex", flexDirection: "row", alignItems: "stretch",
        gap: 16, minHeight: 104,
        // CSS containment — a single card's relayout / repaint /
        // style recalc stays inside its own box. Without this, every
        // hover / focus / state flip on one card forced the whole
        // grid through layout. `layout style paint` covers the three
        // pipelines we care about; `size` is omitted so the card can
        // still grow vertically.
        contain: "layout style paint",
        // Glass morphism — lighter fill + stronger blur so the bg
        // gradient + stars / blobs visibly pass through the card.
        // Matched to the tile cards in TileGrid.
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        background: theme.color.glassFillLite,
        ...warnOverlay,
      }}
    >
      {/* Status / NEW / ok badge — absolutely positioned in the
          card's upper-right corner so it floats over both the
          icon column and the text column without taking row
          space. Same priority cascade: warn > new > ok. */}
      <div style={{ position: "absolute", top: 10, right: 12, zIndex: 1 }}>
        {warn ? (
          <StatusDot tone="warn" size={10} />
        ) : isRecent(item) ? (
          <span style={{
            fontFamily: font.mono, fontSize: 8, fontWeight: 600,
            letterSpacing: "0.10em", textTransform: "uppercase",
            padding: "2px 6px",
            borderRadius: 999,
            background: withAlpha(theme.color.teal, 0.18),
            color: theme.color.teal,
            lineHeight: 1,
          }}>
            new
          </span>
        ) : (
          <StatusDot tone="ok" size={8} />
        )}
      </div>

      {/* Icon column — only renders when the canonical has a
          bundled SVG. Emoji fallback is hidden until the icon set
          catches up; rows without artwork carry on text-only and
          read fine at the row's tight density. */}
      {iconUrl && (
        <div style={{
          display: "flex", alignItems: "center",
          flexShrink: 0,
        }}>
          <img
            src={iconUrl}
            alt=""
            // Native lazy-load + async decode so off-screen cards
            // don't block paint on the visible row, and the browser
            // can decode the icon on a worker thread instead of the
            // main thread. Big win on long pantry scrolls.
            loading="lazy"
            decoding="async"
            // Dropped the filter:drop-shadow — it's a paint-bound
            // CSS filter that re-rasterizes per icon and on every
            // grid scroll frame the GPU has to re-run it for each
            // visible card. The 0.10-alpha shadow was barely
            // perceptible against the glassFill anyway.
            style={{
              width: 60, height: 60, objectFit: "contain",
            }}
          />
        </div>
      )}

      {/* Text column — name + qty/brand + meta row. minWidth: 0
          so the inner flex children honor ellipsis truncation
          rather than overflowing the card. */}
      <div style={{
        flex: 1, minWidth: 0,
        display: "flex", flexDirection: "column",
        justifyContent: "center",
        gap: 2,
        // Right padding so the absolute status badge doesn't
        // overlap long names.
        paddingRight: 22,
      }}>
        {/* Tile-context chip when in search mode — sits above
            the name as a small kicker. */}
        {tileLabel && (
          <div style={{
            fontFamily: font.mono, fontSize: 12,
            letterSpacing: "0.10em", textTransform: "uppercase",
            color: theme.color.inkFaint,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {tileLabel}
          </div>
        )}
        {/* Stack-count badge — only renders when this card is the
            leader of a group of N>1 identical pantry rows (same
            canonical + brand + size + claims + tile). Tap opens
            StackDetailSheet via onOpenStack so the user can act on
            individual instances (drop one, edit expiry on one,
            decrement amount on one, etc.). Singleton groups skip
            this entirely and the card reads as a plain row. */}
        {stackCount > 1 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (onOpenStack) onOpenStack();
            }}
            disabled={!onOpenStack}
            aria-label={`Open ${stackCount} stacked items`}
            className="mcm-focusable"
            style={{
              alignSelf: "flex-start",
              fontFamily: font.mono, fontSize: 12, fontWeight: 700,
              letterSpacing: "0.12em", textTransform: "uppercase",
              color: theme.color.leaf || theme.color.ink,
              background: withAlpha(theme.color.leaf || theme.color.ink, 0.10),
              border: `1px solid ${withAlpha(theme.color.leaf || theme.color.ink, 0.45)}`,
              borderRadius: radius.chip,
              padding: `${space.nudge}px ${space.tight}px`,
              cursor: onOpenStack ? "pointer" : "default",
              marginBottom: 2,
            }}
          >
            ×{stackCount} →
          </button>
        )}
        {/* + ADD BRAND affordance — small UNSET_CHIP-style button
            that sits ABOVE the name when the row has no brand
            (per the CLAUDE.md identity-hierarchy spec: empty
            brand slot inline reads as broken, so the affordance
            goes on its own line above the header). Only renders
            when onUpdate is wired (Showcase mode stays inert).
            Tapping opens the BrandPickerSheet for free-form
            entry + suggestion-list pick. */}
        {!item.brand && updateEnabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setBrandEditing(true);
            }}
            aria-label={`Add a brand to ${item.name}`}
            className="mcm-focusable"
            style={{
              alignSelf: "flex-start",
              fontFamily: font.mono, fontSize: 9,
              color: theme.color.inkMuted,
              background: "transparent",
              border: `1px dashed ${theme.color.hairline}`,
              borderRadius: radius.chip,
              padding: `${space.nudge}px ${space.tight}px`,
              letterSpacing: "0.10em",
              textTransform: "uppercase",
              cursor: "pointer",
              marginBottom: 1,
            }}
          >
            + add brand
          </button>
        )}
        <div style={{
          // New Kansas (Typekit) — italic medium serif. Segmented
          // by axis so the canonical word (the load-bearing
          // identity — "Salami", "Cheddar") renders at full
          // weight 500 while the surrounding axes (brand / state /
          // cut / package size) drop to 300 to read as
          // contextual modifiers around the noun. Falls back to
          // the plain string when the row didn't carry a raw
          // payload (legacy / search-result shapes).
          fontFamily: font.itemTitle, fontStyle: "italic",
          fontSize: 16, lineHeight: 1.15, color: theme.color.ink,
          letterSpacing: "0",
          textTransform: "capitalize",
          marginBottom: 0,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {(() => {
            const parts = item._raw
              ? buildDisplayNameParts(item._raw)
              : null;
            if (!parts || parts.length === 0) {
              return <span style={{ fontWeight: 500 }}>{item.name}</span>;
            }
            return parts.map((p, i) => {
              const isCanonical = p.kind === "canonical" || p.kind === "freeText";
              return (
                <span
                  key={`${p.kind}-${i}`}
                  style={{
                    fontWeight: isCanonical ? 500 : 300,
                    // Light pieces get a softer ink so the eye lands
                    // on the canonical word first.
                    color: isCanonical ? theme.color.ink : theme.color.inkMuted,
                  }}
                >
                  {i > 0 ? " " : ""}{p.text}
                </span>
              );
            });
          })()}
        </div>
        {/* Qty + brand subheader row removed — `item.name` is the
            derived display name from buildDisplayName(), which
            already composes [Brand] [State] [Canonical] [Cut]
            ([Package size]). Showing the brand again as a
            separate cursive line was redundant. */}
        {/* Meta row — category pill on left, days chip on right.
            Renders only when at least one of the two has content,
            so cards without a category and without a date fall
            back to a tighter two-line layout. */}
        {(item.typeLabel || item.days != null || nutrition?.kcal != null) && (
          <div style={{
            display: "flex", alignItems: "center",
            justifyContent: "space-between", gap: 6,
            marginTop: 0,
          }}>
            {item.typeLabel ? (
              <TintedPill
                tone="burnt"
                size="sm"
                style={{ overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {item.typeLabel}
              </TintedPill>
            ) : <span />}
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              flexShrink: 0,
            }}>
              {nutrition?.kcal != null && (() => {
                // Format the per-anchor compactly: "100g" → "/100g",
                // "count" → "/ea", "serving" → "/serv". Keeps the
                // chip honest without ballooning the row.
                const per = (nutrition.per || "100g");
                const compact = per === "count" ? "/ea"
                  : per === "serving" ? "/serv"
                  : `/${per}`;
                return (
                  <span style={{
                    fontFamily: font.mono, fontSize: 10,
                    color: theme.color.inkFaint,
                    letterSpacing: "0.04em",
                    whiteSpace: "nowrap",
                  }}
                  title={`${Math.round(nutrition.kcal)} kcal per ${per}`}
                  >
                    {Math.round(nutrition.kcal)} kcal{compact}
                  </span>
                );
              })()}
              <span style={{
                fontFamily: font.mono, fontSize: 10,
                color: daysChipColor(item.days, theme),
                whiteSpace: "nowrap",
                fontWeight: warn ? 500 : 400,
              }}>
                {formatDaysChip(item.days)}
              </span>
            </div>
          </div>
        )}

        {/* Fill gauge — sealed/opened indicator. Tap to expand
            an inline slider that drags the row's amount between
            0 and max. Tap again (or the ✕) to dismiss. Bar
            tints teal when sealed and burnt when opened to
            match the AddDraftSheet's slider color treatment. */}
        {(() => {
          const max = Number(item.max);
          const amt = Number(item.amount);
          if (!(max > 0) || !Number.isFinite(amt)) return null;
          const pct = Math.max(0, Math.min(100, (amt / max) * 100));
          const sealed = amt >= max - 0.0001;
          const fill = sealed ? theme.color.teal : theme.color.burnt;
          const label = sealed
            ? `Sealed · ${item.qty}`
            : `Opened · ${pct.toFixed(0)}% remaining`;
          // Slider step — 0.1 for small packages so half-units
          // are reachable, 1 for medium, max/100 for big counts.
          const step = max <= 10 ? 0.1 : max <= 100 ? 1 : max / 100;
          const fmt = (n) => Number.isInteger(n) ? String(n) : Number(n).toFixed(1);
          return (
            <>
              <button
                type="button"
                onClick={(e) => {
                  if (!updateEnabled) return;
                  e.stopPropagation();
                  setFillEditing(prev => !prev);
                }}
                aria-label={`Adjust ${item.name} amount — currently ${label}`}
                title={updateEnabled ? "Tap to adjust how much is left" : label}
                disabled={!updateEnabled}
                style={{
                  width: "100%",
                  // Padding expands the tap target without
                  // moving the visual bar — 8px above + below
                  // the 4px bar gives a ~20px touch zone, still
                  // under Apple's 44px guideline but workable
                  // for a row-density inline control.
                  padding: "8px 0",
                  marginTop: -4,
                  background: "transparent",
                  border: "none",
                  cursor: updateEnabled ? "pointer" : "default",
                  display: "block",
                }}
              >
                <div
                  style={{
                    height: 4,
                    borderRadius: 2,
                    background: withAlpha(theme.color.ink, 0.06),
                    overflow: "hidden",
                  }}
                >
                  <div style={{
                    height: "100%", width: `${pct}%`,
                    background: fill,
                    boxShadow: `0 0 6px ${withAlpha(fill, 0.45)}`,
                    transition: "width 600ms cubic-bezier(0.22, 1, 0.36, 1), background 200ms ease",
                  }} />
                </div>
              </button>
              {updateEnabled && fillEditing && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 8,
                    padding: "8px 10px",
                    background: withAlpha(theme.color.ink, 0.04),
                    borderRadius: 10,
                    border: `1px solid ${theme.color.hairline}`,
                  }}
                >
                  <input
                    type="range"
                    min="0" max={max} step={step}
                    value={amt}
                    onChange={(e) => {
                      const newAmount = Number(e.target.value);
                      const wasSealed = amt >= max - 0.0001;
                      const isOpening = wasSealed && newAmount < max;
                      const patch = { amount: newAmount };
                      // First open — re-anchor the freshness clock
                      // off "now" against the canonical's
                      // opened-shelf-life window. Only fires when
                      // the canonical actually ships opened data;
                      // without it, the original sealed expiry
                      // stays in place (which is honest — we
                      // don't know how fast it spoils once
                      // opened, so we don't fake a date).
                      if (isOpening) {
                        const days = shelfLifeFor(
                          item.canonicalId,
                          item._location || item._raw?.location,
                          { opened: true }
                        );
                        if (Number.isFinite(days) && days > 0) {
                          const d = new Date();
                          d.setDate(d.getDate() + days);
                          d.setHours(23, 59, 0, 0);
                          patch.expiresAt = d;
                        }
                      }
                      onUpdate(item, patch);
                    }}
                    aria-label={`Estimate ${item.name} remaining`}
                    style={{
                      flex: 1,
                      accentColor: fill,
                    }}
                  />
                  <span style={{
                    fontFamily: font.mono, fontSize: 10,
                    color: theme.color.inkMuted,
                    minWidth: 64, textAlign: "right",
                    whiteSpace: "nowrap",
                  }}>
                    {fmt(amt)} / {fmt(max)} {item.unit || ""}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setFillEditing(false); }}
                    aria-label="Close slider"
                    style={{
                      width: 22, height: 22,
                      background: "transparent",
                      border: `1px solid ${theme.color.hairline}`,
                      color: theme.color.inkMuted,
                      borderRadius: 999,
                      fontFamily: font.mono, fontSize: 10,
                      cursor: "pointer", flexShrink: 0,
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}
            </>
          );
        })()}
      </div>
    </GlassPanel>
    </div>
    {/* BACK FACE — focus-mode action menu. Hidden via
        backfaceVisibility while the card is at 0deg; revealed at
        180deg. Tap on the empty area exits focus mode; tap on a
        button runs the action (and exits focus, except for delete
        which unmounts the row instead). pointerEvents are gated on
        the flipped state so the back doesn't intercept taps while
        invisible. */}
    <div
      onClick={handleBackPanelClick}
      style={{
        position: "absolute",
        inset: 0,
        transform: "rotateY(180deg)",
        backfaceVisibility: "hidden",
        WebkitBackfaceVisibility: "hidden",
        // Pointer events stay OFF until backInteractive flips true
        // after the post-flip lockout. This swallows the synthetic
        // click that fires when the user releases their long-press —
        // otherwise it lands on whichever back-face button happens
        // to be under their finger and runs the wrong action.
        pointerEvents: flipped && backInteractive ? "auto" : "none",
        borderRadius: 22,
        background: theme.color.glassFillLite,
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        border: `1px solid ${theme.color.glassBorder}`,
        boxShadow: theme.shadow.glass,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: 16,
        ...NO_SELECT_STYLE,
      }}
    >
      <div style={{
        fontFamily: font.mono, fontSize: 9,
        letterSpacing: "0.12em", textTransform: "uppercase",
        color: theme.color.inkFaint,
      }}>
        Quick actions
      </div>
      <div style={{
        display: "flex",
        flexDirection: "row",
        gap: 8,
        flex: 1,
        alignItems: "stretch",
      }}>
        {/* Add to Recipe — pin / unpin this canonical for the next
            AI Recipe generation. Disabled when no canonical bound. */}
        <button
          type="button"
          onClick={handlePinTap}
          disabled={!canonicalId}
          aria-label={pinned ? "Unpin from next AI cook" : "Pin to next AI cook"}
          style={{
            flex: 1,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            gap: 4,
            padding: "10px 8px",
            borderRadius: 14,
            border: `1px solid ${pinned ? theme.color.teal : withAlpha(theme.color.teal, 0.45)}`,
            background: pinned
              ? withAlpha(theme.color.teal, 0.28)
              : withAlpha(theme.color.teal, 0.12),
            color: theme.color.teal,
            fontFamily: font.mono, fontSize: 10,
            fontWeight: 700, letterSpacing: "0.08em",
            textTransform: "uppercase",
            cursor: canonicalId ? "pointer" : "not-allowed",
            opacity: canonicalId ? 1 : 0.45,
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>{pinned ? "✓" : "+"}</span>
          <span>{pinned ? "Pinned" : "To Recipe"}</span>
        </button>

        {/* Restock — append this row to the shopping list. Always
            available; even free-text rows can be restocked by name. */}
        <button
          type="button"
          onClick={handleRestockTap}
          disabled={!onRestock}
          aria-label="Add to shopping list"
          style={{
            flex: 1,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            gap: 4,
            padding: "10px 8px",
            borderRadius: 14,
            border: `1px solid ${withAlpha(theme.color.storedIn || "#7eb8d4", 0.55)}`,
            background: withAlpha(theme.color.storedIn || "#7eb8d4", 0.14),
            color: theme.color.storedIn || "#7eb8d4",
            fontFamily: font.mono, fontSize: 10,
            fontWeight: 700, letterSpacing: "0.08em",
            textTransform: "uppercase",
            cursor: onRestock ? "pointer" : "not-allowed",
            opacity: onRestock ? 1 : 0.45,
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>↻</span>
          <span>Restock</span>
        </button>

        {/* Delete — two-tap arm-then-commit. First tap arms (color
            shift, label flips); second tap within DELETE_ARM_MS
            commits and unmounts the row. */}
        <button
          type="button"
          onClick={handleDeleteTap}
          disabled={!onDelete}
          aria-label={deleteArmed ? "Tap again to delete" : "Delete this item"}
          style={{
            flex: 1,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            gap: 4,
            padding: "10px 8px",
            borderRadius: 14,
            border: `1px solid ${deleteArmed ? theme.color.burnt : withAlpha(theme.color.burnt, 0.45)}`,
            background: deleteArmed
              ? "linear-gradient(180deg, #C85A1F 0%, #A34711 100%)"
              : withAlpha(theme.color.burnt, 0.12),
            color: deleteArmed ? "#FFF8EE" : theme.color.burnt,
            fontFamily: font.mono, fontSize: 10,
            fontWeight: 700, letterSpacing: "0.08em",
            textTransform: "uppercase",
            cursor: onDelete ? "pointer" : "not-allowed",
            opacity: onDelete ? 1 : 0.45,
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>{deleteArmed ? "‼" : "✕"}</span>
          <span>{deleteArmed ? "Confirm" : "Delete"}</span>
        </button>
      </div>
      <div style={{
        fontFamily: font.detail, fontStyle: "italic",
        fontSize: 10, color: theme.color.inkFaint,
        textAlign: "center",
      }}>
        Tap empty area to exit
      </div>
    </div>
    </motion.div>
    </div>
    {brandEditing && (
      <BrandPickerSheet
        canonicalId={item?.canonicalId || null}
        suggestions={brandSuggestions}
        onPick={(brand) => {
          setBrandEditing(false);
          if (updateEnabled) onUpdate(item, { brand });
        }}
        onClose={() => setBrandEditing(false)}
      />
    )}
    </>
  );
}

export const KitchenCard = memo(KitchenCardImpl);
